/**
 * Keeps `remark-math`'s single-dollar inline rule from eating prose that merely
 * contains two dollar amounts.
 *
 * Bird writes a lot of inline LaTeX unprompted — a geochemistry pilot's answers
 * are dense with `$SiO_2$`, `$\mu^{66}Zn$`, `$10.6\text{ wt}\%$` — so chat has
 * to render `$…$`, not just `$$…$$`. But `remark-math` treats the FIRST and
 * SECOND `$` in a paragraph as a delimiter pair regardless of what sits between
 * them, which is measurably wrong for currency:
 *
 *     "The plan costs $5 and the other costs $10 per month."
 *       → inlineMath("5 and the other costs ")
 *
 * That renders as garbled maths in the middle of a sentence. One amount alone
 * is safe (no closing delimiter), so the failure needs two — which is exactly
 * the shape a pricing or billing answer has.
 *
 * This plugin runs AFTER `remark-math` and demotes any `inlineMath` node that
 * carries no evidence of actually being maths back to the literal source text.
 * Operating on the AST rather than pre-processing the raw string is what keeps
 * it safe: `remark-math` has already excluded fenced and inline code, so
 * `` `$HOME` `` and shell snippets never reach here in the first place.
 *
 * ## What counts as evidence
 *
 * A node is maths when ANY of these hold:
 *
 * - it contains a LaTeX control character (`\`, `^`, `_`, `{`, `}`) — covers
 *   `\delta^{66}Zn`, `SiO_2`, `10.6\text{ wt}\%`;
 * - it contains a relational or arithmetic operator (`= + * / < >`) — covers
 *   `x + y`, `E = mc^2`;
 * - it contains no whitespace at all — covers bare symbols like `FeO`, `MgO`,
 *   `n`, which are the most common inline maths Bird emits and are never
 *   mistakable for a currency run (a currency run always spans prose words).
 *
 * `-` is deliberately NOT an operator here: it reads as a range or a hyphen far
 * more often than as subtraction, and `$100 - $200` is the exact false positive
 * this exists to stop. `/` and `+` ARE operators, which is why the prose veto
 * above them exists: `$49/month … $99/month` is a real Bower price sentence and
 * the operator rung alone rendered it as maths.
 *
 * The trade is explicit: `$a - b$` (subtraction, spaced, no other signal) is
 * demoted and shown literally. That is the strictly safer direction — a raw
 * `$a - b$` is legible, whereas "costs 5 and the other costs" rendered as an
 * equation is not, and it corrupts a sentence the user did not write in LaTeX.
 */

import type { Parent, RootContent } from 'mdast';

/** LaTeX control characters. Their presence is conclusive. */
const LATEX_CONTROL_RE = /[\\^_{}]/;

/** Relational / arithmetic operators. Excludes `-` — see the module docblock. */
const MATH_OPERATOR_RE = /[=+*/<>]/;

/**
 * Prose caught between two currency amounts, which the operator rung would
 * otherwise wave through: a pricing sentence contains `/` ("$49/month") or `+`
 * ("$5 + $10 in fees") inside the span, and those are the amounts Bower itself
 * quotes. Recognised by shape rather than by currency symbol — a span that
 * OPENS with a digit and then runs on into ordinary words is a sentence, not a
 * formula.
 *
 * Two alphabetic words is the floor: `49/month and Plan B is ` has four, while
 * a real expression that starts with a digit (`2x + 3y`, `10 \times n`) has
 * none, because its tokens carry digits or backslashes.
 *
 * A leading digit is required for BOTH rungs, so `x + y` and `a = b` are never
 * touched — only spans that open on an amount.
 */
const PROSE_RUN_RE = /^\d/;
const ALPHA_WORD_RE = /^[A-Za-z]+$/;
const MIN_PROSE_WORDS = 2;

/**
 * A span left DANGLING on an operator, which "$5 + $10 in fees" produces
 * (`5 + `). No complete expression ends on its operator, so this is the tell
 * for a currency pair whose prose sits outside the span rather than inside it
 * — the case the word count above cannot see, because there are no words.
 */
const DANGLING_OPERATOR_RE = /[=+*/<>-]$/;

function looksLikeProse(value: string): boolean {
  const trimmed = value.trim();
  if (!PROSE_RUN_RE.test(trimmed)) return false;
  if (DANGLING_OPERATOR_RE.test(trimmed)) return true;
  const words = trimmed.split(/\s+/).filter((w) => ALPHA_WORD_RE.test(w));
  return words.length >= MIN_PROSE_WORDS;
}

/**
 * `remark-math` adds `inlineMath` to mdast, but the shipped `mdast` types don't
 * declare it, so it arrives as a plain node carrying `value`.
 */
interface InlineMathNode {
  type: 'inlineMath';
  value: string;
  position?: RootContent['position'];
}

function isInlineMath(node: { type: string }): node is InlineMathNode {
  return node.type === 'inlineMath';
}

/**
 * Whether an `inlineMath` node's contents look like maths rather than a stretch
 * of prose caught between two currency amounts.
 *
 * Exported for direct testing — the cases that matter are string-level, and
 * asserting them here is far clearer than round-tripping every one through a
 * full markdown parse.
 */
export function looksLikeMath(value: string): boolean {
  // A LaTeX control character is conclusive and outranks the prose veto —
  // `10.6\text{ wt}\%` opens with a digit and contains a word.
  if (LATEX_CONTROL_RE.test(value)) return true;
  // Checked BEFORE the operator rung, which is the rung currency slips through.
  if (looksLikeProse(value)) return false;
  if (MATH_OPERATOR_RE.test(value)) return true;
  // No whitespace: a bare symbol (FeO, n, MgO). A currency run always spans
  // prose words and therefore always contains whitespace.
  return value.trim().length > 0 && !/\s/.test(value);
}

/**
 * The literal source this node was parsed from.
 *
 * Sliced from the original document by offset so the reconstruction is exact —
 * re-wrapping `node.value` in `$…$` would be a guess about the delimiter that
 * was actually used. Falls back to that guess only when position data is
 * missing (no remark plugin in this pipeline strips it, but the type allows it).
 */
function originalText(node: InlineMathNode, source: string): string {
  const start = node.position?.start.offset;
  const end = node.position?.end.offset;
  if (typeof start === 'number' && typeof end === 'number' && end > start) {
    return source.slice(start, end);
  }
  return `$${node.value}$`;
}

/**
 * Remark plugin. Must be registered AFTER `remarkMath` so there are
 * `inlineMath` nodes to inspect.
 */
export function remarkDemoteCurrencyMath() {
  return (tree: Parent, file: { value?: unknown }): void => {
    const source = typeof file.value === 'string' ? file.value : '';

    const walk = (parent: Parent): void => {
      const children = parent.children as Array<RootContent | InlineMathNode>;
      for (let i = 0; i < children.length; i += 1) {
        const child = children[i];
        if (isInlineMath(child)) {
          if (!looksLikeMath(child.value)) {
            children[i] = {
              type: 'text',
              value: originalText(child, source),
              position: child.position,
            } as RootContent;
          }
          continue;
        }
        if ('children' in child && Array.isArray((child as Parent).children)) {
          walk(child as Parent);
        }
      }
    };

    walk(tree);
  };
}
